import { useState, useMemo, useCallback, useEffect } from 'react';

// Utils
import useSubscriptionClick from '../useSubscriptionClick';
import useFetchIntervalData from '../useFetchIntervalData';
import { getStandings } from '../../services/standings/standings';
import { mapToOptions } from '../../utils';
import { normalizeStandingsResponseData } from '../../normalizers/src/standings/normalizeStandingsResponseData';

// Types
import { IGeneralSubscriberInfo } from '../../constants/subscribeTypes';
import { GROUP_TYPES } from '../../constants/standings';
import { RefreshTimes, SDKOptions } from '../../types';
import { IStandingsConfig, IStandingsRawResponse } from '../../types/standings';

interface IUseStandingsArgs extends IGeneralSubscriberInfo {
  stage_id?: string;
  dataRefreshTime?: RefreshTimes;
  dataHeaderDefaultOption?: string;
  dataLabels?: IStandingsConfig['dataLabels'];
  dataEntityLinks?: IStandingsConfig['dataEntityLinks'];
  dataTeamNameType?: IStandingsConfig['dataTeamNameType'];
  dataLimit?: IStandingsConfig['dataLimit'];
  dataOffset?: IStandingsConfig['dataOffset'];
  sport: string;
  sdkOptions: SDKOptions;
}

export const useStandings = ({
  stage_id,
  dataRefreshTime,
  dataHeaderDefaultOption = GROUP_TYPES.OVERALL,
  dataWidgetCuid,
  dataWidgetId,
  dataLabels,
  dataEntityLinks,
  dataTeamNameType,
  subscribers,
  dataWidgetSport,
  dataLimit,
  dataOffset,
  sport,
  sdkOptions,
}: IUseStandingsArgs) => {
  const [isLoading, setIsLoading] = useState(false);
  const [standings, setStandings] = useState();
  const [groupTypes, setGroupTypes] = useState();
  const [groupType, setGroupType] = useState('');

  const params = {
    stage_id,
    sport,
    limit: dataLimit,
    offset: dataOffset,
  };

  const fetchStandings = useCallback(
    (manageLoader: boolean) => {
      if (stage_id) {
        manageLoader && setIsLoading(true);
        getStandings({ params, sdkOptions })
          ?.then((response) => {
            manageLoader && setIsLoading(false);
            const standingsData = response.data
              .map((data: Record<string, IStandingsRawResponse[]>) => data.standings.map((standing: IStandingsRawResponse) => standing))
              .flat();
            const standingsGroupTypes = response.data
              .map((data: Record<string, IStandingsRawResponse[]>) => data.group_types.map((group: IStandingsRawResponse) => group))
              .flat();

            setStandings(standingsData);
            setGroupTypes(standingsGroupTypes);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    },
    [stage_id, dataLimit, dataOffset],
  );

  useEffect(() => {
    fetchStandings(true);
  }, [fetchStandings]);

  useFetchIntervalData(fetchStandings, dataRefreshTime);

  const groupTypeOptions = useMemo(() => mapToOptions(groupTypes), [groupTypes]);

  useEffect(() => {
    if (groupTypeOptions && dataHeaderDefaultOption) {
      const selectedGroup = groupTypeOptions.find((group) => group.label.toLocaleUpperCase() === dataHeaderDefaultOption)?.value;
      const defaultGroup = groupTypeOptions[0]?.value;
      setGroupType(selectedGroup ? selectedGroup : defaultGroup);
    }
  }, [groupTypeOptions, dataHeaderDefaultOption]);

  const normalizeStandings = useMemo(() => {
    const selectedGroup = groupTypeOptions.find((group) => group.value === groupType)?.type ?? GROUP_TYPES.OVERALL;
    return normalizeStandingsResponseData(sport, standings, dataLabels, dataEntityLinks, dataTeamNameType)?.filter(
      ({ group }) => group.type === selectedGroup,
    );
  }, [standings, groupType, groupTypeOptions, dataLabels, sport]);

  return {
    isLoading,
    standings: normalizeStandings,
    groupType,
    setGroupType,
    groupTypeOptions,
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
  };
};
